/**
 * Cache identity for workspace trigger profiles, their run history, and the
 * signals that arrived against them.
 *
 * Typed factories rather than inline arrays so the query and every
 * invalidation that follows a mutation cannot drift apart (constitution
 * §2.6). Every key is scoped by workspace: a trigger profile belongs to
 * exactly one.
 *
 * `all` exists alongside `list`/`detail`/`runs` as the broad invalidation
 * target for mutations that should refresh everything about a profile (e.g.
 * enabling/disabling touches both its list row and its own detail read).
 */
export const triggerKeys = {
  all: (workspaceId: string) => ['trigger-profiles', workspaceId] as const,
  list: (workspaceId: string) =>
    ['trigger-profiles', workspaceId, 'list'] as const,
  detail: (workspaceId: string, triggerProfileId: string) =>
    ['trigger-profiles', workspaceId, 'detail', triggerProfileId] as const,
  runs: (workspaceId: string, triggerProfileId: string) =>
    ['trigger-profiles', workspaceId, 'runs', triggerProfileId] as const,
  /** A profile's signals — what *arrived*, as distinct from `runs`, which is
   *  what fired. Two keys because they are two reads and two facts; a signal
   *  that produced no run is exactly the case the surface exists for. */
  signals: (workspaceId: string, triggerProfileId: string) =>
    ['trigger-profiles', workspaceId, 'signals', triggerProfileId] as const,
  /** A driver `type`'s variable manifest (spec 074, US3) — static and
   *  workspace-independent in content, but the read endpoint is still
   *  workspace-scoped (route-prefix/auth consistency, research.md D-MAN1),
   *  so the key carries `workspaceId` for cache-scoping consistency with
   *  every other key here, even though it never actually varies by it. */
  manifest: (workspaceId: string, driverType: string) =>
    ['trigger-profiles', workspaceId, 'variable-manifest', driverType] as const,
};
